import { css, jsx } from '@emotion/react';
import { useState, useMemo } from 'react';
import { useIsSSR } from 'react-aria';
import { Select as Select$1, Button, SelectValue, Popover, ListBox } from 'react-aria-components';
import { Description } from '../Description/Description.es.js';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage.es.js';
import { Label } from '../Label/Label.es.js';
import { SelectGroup } from './SelectGroup.es.js';
import { SelectItem } from './SelectItem.es.js';
import { Colors, Typography, Shadows, VisuallyHidden, Icon } from '@zapier/design-system';
import { SelectContext } from './SelectContext.es.js';

const SelectStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--zds-space-4, 5px)",
  "&[data-invalid] > button": {
    border: `1px solid var(--zds-status-error, ${Colors.StatusErrorStronger})`
  }
});
const SelectTriggerStyles = css(Object.assign(Object.assign({ paddingLeft: "var(--zds-space-8, 15px)", paddingRight: "var(--zds-space-8, 5px)", display: "flex", justifyContent: "space-between", alignItems: "center", height: "var(--zds-size-medium, 42px)", width: "100%", backgroundColor: `var(--zds-background-weaker, ${Colors.PrimeWhite})`, borderRadius: "var(--zds-radius-small, 3px)", border: `1px solid var(--zds-stroke-default, ${Colors.GrayWarm6})`, textAlign: "left", cursor: "pointer" }, Typography.Body2), { '&[data-size="large"]': {
  height: "var(--zds-size-large, 48px)"
}, "&[data-focused], &[data-focus-visible]": {
  outline: `var(--zds-border-focus, 2px solid ${Colors.UiPrimaryStrongest})`,
  outlineOffset: "-1px"
}, "&[data-pressed]": {
  backgroundColor: `var(--zds-background-default, ${Colors.GrayWarm2})`
}, "&[data-disabled]": {
  cursor: "not-allowed",
  backgroundColor: `var(--zds-background-strongest, ${Colors.GrayWarm2})`,
  color: `var(--zds-text-weakest, ${Colors.GrayWarm5})`,
  border: `1px solid var(--zds-stroke-weaker, ${Colors.GrayWarm4})`
} }));
const SelectValueStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--zds-space-8, 8px)",
  paddingRight: "var(--zds-space-12, 10px)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: `var(--zds-text-default, ${Colors.GrayWarm9})`,
  "&[data-placeholder]": {
    color: `var(--zds-text-weakest, ${Colors.GrayWarm7})`
  },
  "&[data-size=compact]": Object.assign({}, Typography.MinimalPrint2)
});
const PopoverStyles = css({
  maxHeight: 215,
  overflow: "auto",
  padding: "var(--zds-space-8, 10px)",
  boxSizing: "border-box",
  backgroundColor: `var(--zds-background-weaker, ${Colors.PrimeWhite})`,
  border: `1px solid var(--zds-stroke, weakest, ${Colors.GrayWarm3})`,
  boxShadow: Shadows.elevation20,
  borderRadius: "var(--zds-radius-small, 3px)",
  width: "var(--trigger-width)"
});
const ListBoxStyles = css({
  outline: "none"
});
const Select = ({ "aria-describedby": ariaDescribedBy, "aria-details": ariaDetails, children, className, defaultSelectedValue, isDisabled = false, errorMessage, helpText, isInvalid = false, isLabelHidden = false, isOpen, isRequired = false, label, onBlur, onFocus, onFocusChange, onKeyDown, onKeyUp, onOpenChange, onValueChange, placeholder, selectedValue, size = "medium" }) => {
  const isSSR = useIsSSR();
  const [disabledKeys, setDisabledKeys] = useState([]);
  const registerDisabledItem = useMemo(() => ({
    registerDisabledItem: (id) => {
      setDisabledKeys((prev) => [...prev, id]);
    }
  }), []);
  if (isSSR) {
    return null;
  }
  return jsx(
    SelectContext.Provider,
    { value: registerDisabledItem },
    jsx(
      Select$1,
      { css: SelectStyles, "aria-details": ariaDetails, "aria-describedby": ariaDescribedBy, className, "data-zds": true, defaultSelectedKey: defaultSelectedValue, disabledKeys, isDisabled, isInvalid, isOpen, onBlur, onFocus, onFocusChange, onKeyDown, onKeyUp, onOpenChange, onSelectionChange: onValueChange, placeholder, selectedKey: selectedValue, isRequired },
      isLabelHidden ? jsx(
        VisuallyHidden,
        null,
        jsx(Label, null, label)
      ) : jsx(Label, { isCompact: size === "compact", isRequired, isDisabled, isInvalid }, label),
      jsx(
        Button,
        { css: SelectTriggerStyles, "data-size": size, "data-zds": true },
        jsx(SelectValue, { "data-size": size, css: SelectValueStyles, "data-zds": true }),
        jsx(Icon, { name: "arrowDropdown", color: "TextWeaker", size: size === "compact" ? 20 : 24 })
      ),
      isInvalid && errorMessage ? jsx(ErrorMessage, { isCompact: size === "compact" }, errorMessage) : helpText && jsx(Description, { isCompact: size === "compact" }, helpText),
      jsx(
        Popover,
        { css: PopoverStyles, "data-zds": true },
        jsx(ListBox, { css: ListBoxStyles, "data-zds": true }, children)
      )
    )
  );
};
Select.Item = SelectItem;
Select.Group = SelectGroup;

export { Select };
//# sourceMappingURL=Select.es.js.map
